import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@tellescope/ui";
import { useTellescopeQuery } from "@tellescope/tanstack-query";
import { ChargebeeFieldProps } from "./ChargebeeField";
import { getFirstAddressAnswer } from "./getFirstAddressAnswer";
import { resolveErrorMessage } from "../../utils";

const PUBLIC_FORM_ADDRESS_ERROR =
  "A complete address question is required before a Chargebee payment field on public forms";

export interface ChargebeeFieldContentProps extends Omit<ChargebeeFieldProps, "title"> {}

export const ChargebeeFieldContent = ({
  id,
  value,
  onChange,
  enduserId,
  formResponses,
  options,
  isOptional,
  session,
}: ChargebeeFieldContentProps) => {
  const { t } = useTranslation("forms");
  const [verifyError, setVerifyError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [completed, setCompleted] = useState(false);

  const detailsQuery = useTellescopeQuery({
    model: "form_responses",
    method: "chargebee_details",
    request: {
      fieldId: id,
      billingAddress: getFirstAddressAnswer(formResponses),
      enduserId,
    },
    session,
    // Each call can create a Chargebee customer and mutate the enduser server-side, so never
    // re-fetch for a field that is already paid.
    enabled: !value,
  });

  const url = detailsQuery.data?.url ?? "";

  const genericFetchError = t("Something went wrong loading the payment form. Please try again.");
  const fetchErrorMessage = detailsQuery.error
    ? resolveErrorMessage(detailsQuery.error, genericFetchError) === PUBLIC_FORM_ADDRESS_ERROR
      ? PUBLIC_FORM_ADDRESS_ERROR
      : genericFetchError
    : "";

  // The hosted page is cross-origin, so its second load — the navigation to the redirect_url once
  // payment completes — is the only available completion signal. Load 1 is the checkout page.
  const [loadCount, setLoadCount] = useState(0);
  const handleLoad = () => {
    const nextCount = loadCount + 1;
    setLoadCount(nextCount);
    if (nextCount !== 2 || completed) return;
    setCompleted(true);
    onChange({ url });
  };

  const handleVerify = async () => {
    setVerifying(true);
    setVerifyError("");
    try {
      const { hasPaymentMethod } = await session.api.form_responses.chargebee_details({
        fieldId: id,
        verify: true,
        enduserId,
      });
      if (hasPaymentMethod) {
        setCompleted(true);
        onChange({ url: "verified" });
        return;
      }
      setVerifyError(t("No payment method found. Please add a card and try again."));
    } catch (err: unknown) {
      setVerifyError(resolveErrorMessage(err, t("Failed to verify payment method")));
    } finally {
      setVerifying(false);
    }
  };

  const collectOnly = !!options?.chargebeeCollectPaymentMethodOnly;

  if (value || completed) {
    return (
      <div className="flex items-center justify-center min-h-3 gap-1">
        <CheckCircle2 color="white" fill="green" />
        <p className="text-sm">
          {collectOnly ? t("Your payment method was saved successfully") : t("Your purchase was successful")}
        </p>
      </div>
    );
  }

  if (!url && fetchErrorMessage) {
    return <p className="text-sm text-destructive">{fetchErrorMessage}</p>;
  }

  if (!url) {
    return <p className="text-sm">{t("Loading payment form...")}</p>;
  }

  const activeError = verifyError || fetchErrorMessage;

  return (
    <div className="space-y-1">
      <iframe src={url} title="Checkout" className="w-full border-none" style={{ height: 700 }} onLoad={handleLoad} />
      {collectOnly && !isOptional && (
        <Button onClick={handleVerify} loading={verifying}>
          {t("Done")}
        </Button>
      )}
      {!!activeError && <p className="text-sm text-destructive">{activeError}</p>}
    </div>
  );
};
